import React, { Suspense, useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { useLazyLoadQuery } from 'react-relay';
import { useRouterActions } from 'react-resource-router';
import { graphql } from 'relay-runtime';

import Button from '@atlaskit/button/button';
import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import { useHubContext } from '@atlassian/company-hub-common';
import { useShouldShowPremiumUpsell } from '@atlassian/company-hub-common/use-should-show-premium-upsell';

import { getRequestCount } from '../../../services/get-request-count';
import type { navLinksQuery } from './__generated__/navLinksQuery.graphql';
import messages from './messages';
import { MoreDropdown } from './more-dropdown';
import { PortalDropdown } from './portal-dropdown';
import { SiteNavigation } from './site-navigation';
import { TopicDropdown } from './topic-dropdown';

const styles = cssMap({
	countBadge: {
		height: '20px',
		minWidth: '20px',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('color.background.neutral.bold'),
		color: token('color.text.inverse'),
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
	},
	// Fill leftover navbar space without width:100% (Inline grow="fill"), which
	// overflows the logo + profile siblings and pushes the avatar off the bar.
	siteTreeNavLinks: {
		flexGrow: 1,
		flexShrink: 1,
		minWidth: 0,
	},
});

type RequestCountData = {
	approvals: number;
	pendingApprovals: number;
	request: number;
};

// Module-level cache so repeated mount/unmount cycles don't re-trigger the
// network request when the data has already been fetched for this hub.
// Only used when hubs_fix_rerendering_bootstrap_page_media is ON.
const requestCountCache = new Map<string, RequestCountData>();
const getCacheKey = (cloudUrl: string, helpCenterAri: string) => `${cloudUrl}::${helpCenterAri}`;

/**
 * Test-only helper: clears the module-level request count cache so unit tests
 * can simulate a "first mount with no cached data" scenario without resetting
 * the entire jest module registry.
 */
export const __resetRequestCountCacheForTests = (): void => {
	requestCountCache.clear();
};

const NavLinkItems = ({
	backgroundColor = token('color.background.neutral.bold'),
	requestCount,
	textColor = token('color.text.inverse'),
}: {
	backgroundColor?: string;
	requestCount: RequestCountData | null;
	textColor?: string;
}) => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const {
		cloudId,
		env: { helpCenterAri, hubEditMode },
		featureFlags,
	} = useHubContext();
	const isDraftModeEnabled =
		fg('help_hubs_draft_mode') && featureFlags?.help_hubs_draft_mode === true;
	const isConfigurableSiteTreeEnabled =
		fg('help_hubs_configurable_site_tree') &&
		featureFlags?.help_hubs_configurable_site_tree === true;
	// Gates the topics fetch as well as the menu, so hubs without the feature do
	// not pay for a field nothing renders.
	const isTopicsEnabled = fg('hubs_premium_upsell');
	// Folders and custom pages are Premium-only, so un-entitled tenants get no
	// entry point to them — from the site tree or the legacy More dropdown.
	const isPremiumLocked = useShouldShowPremiumUpsell();
	const fetchDraftPages = Boolean(hubEditMode) && isDraftModeEnabled;

	const data = useLazyLoadQuery<navLinksQuery>(
		graphql`
			query navLinksQuery(
				$cloudId: ID!
				$hubAri: ID!
				$isDraft: Boolean
				$isConfigurableSiteTreeEnabled: Boolean!
				$isTopicsEnabled: Boolean!
			) {
				helpCenter(cloudId: $cloudId) {
					helpCenterById(helpCenterAri: $hubAri, isDraft: $isDraft)
						@optIn(to: "HelpCenterAggBeta") {
						__typename
						... on HelpCenter {
							...portalDropdown_AtlassianHubAppShell @dangerously_unaliased_fixme
							...topicDropdown_AtlassianHubAppShell
								@dangerously_unaliased_fixme
								@include(if: $isTopicsEnabled)
							...moreDropdown_AtlassianHubAppShell
								@dangerously_unaliased_fixme
								@skip(if: $isConfigurableSiteTreeEnabled)
							...siteNavigation_AtlassianHubAppShell
								@dangerously_unaliased_fixme
								@include(if: $isConfigurableSiteTreeEnabled)
						}
					}
				}
			}
		`,
		{
			cloudId,
			hubAri: helpCenterAri,
			isDraft: fetchDraftPages,
			isConfigurableSiteTreeEnabled,
			isTopicsEnabled,
		},
	);

	const helpCenterById = data?.helpCenter?.helpCenterById;
	const helpCenterRef = helpCenterById?.__typename === 'HelpCenter' ? helpCenterById : null;

	const handleHomeClick = useCallback(() => {
		push('/');
	}, [push]);

	const handleRequestsClick = useCallback(() => {
		push('/user/requests');
	}, [push]);

	const handleApprovalsClick = useCallback(() => {
		push('/user/approvals');
	}, [push]);

	if (!requestCount || !helpCenterById) {
		return null;
	}

	return (
		<Inline
			testId="nav-links"
			space="space.050"
			xcss={isConfigurableSiteTreeEnabled ? styles.siteTreeNavLinks : undefined}
			alignInline={isConfigurableSiteTreeEnabled ? 'center' : undefined}
		>
			<Button appearance="subtle" onClick={handleHomeClick} style={{ color: textColor }}>
				{formatMessage(messages.home)}
			</Button>

			<PortalDropdown queryRef={helpCenterRef} textColor={textColor} />

			{/* Must follow the query's @include(if: $isTopicsEnabled): with the gate
			    off the fragment is never spread, so mounting the menu would hand
			    useFragment a HelpCenter record with no ref and Relay would throw. */}
			{isTopicsEnabled && <TopicDropdown queryRef={helpCenterRef} textColor={textColor} />}

			<Button appearance="subtle" onClick={handleRequestsClick} style={{ color: textColor }}>
				<Inline alignBlock="center" space="space.075">
					{formatMessage(messages.requests)}
					{requestCount.request > 0 && (
						<Box
							as="span"
							style={{ backgroundColor: textColor, color: backgroundColor }}
							xcss={styles.countBadge}
						>
							{requestCount.request}
						</Box>
					)}
				</Inline>
			</Button>

			{(requestCount.approvals > 0 || requestCount.pendingApprovals > 0) && (
				<Button appearance="subtle" onClick={handleApprovalsClick} style={{ color: textColor }}>
					<Inline alignBlock="center" space="space.075">
						{formatMessage(messages.approvals)}
						{requestCount.pendingApprovals > 0 && (
							<Box
								as="span"
								style={{ backgroundColor: textColor, color: backgroundColor }}
								xcss={styles.countBadge}
							>
								{requestCount.pendingApprovals}
							</Box>
						)}
					</Inline>
				</Button>
			)}

			{!isPremiumLocked &&
				(isConfigurableSiteTreeEnabled ? (
					<SiteNavigation queryRef={helpCenterRef} textColor={textColor} />
				) : (
					<MoreDropdown queryRef={helpCenterRef} textColor={textColor} />
				))}
		</Inline>
	);
};

export const NavLinks = ({
	backgroundColor = token('color.background.neutral.bold'),
	textColor = token('color.text.inverse'),
}: {
	backgroundColor?: string;
	textColor?: string;
}): JSX.Element | null => {
	const {
		cloudUrl,
		env: { helpCenterAri },
	} = useHubContext();

	const [requestCount, setRequestCount] = useState<RequestCountData | null>(() =>
		fg('hubs_fix_rerendering_bootstrap_page_media')
			? (requestCountCache.get(getCacheKey(cloudUrl, helpCenterAri)) ?? null)
			: null,
	);

	useEffect(() => {
		if (fg('hubs_fix_rerendering_bootstrap_page_media')) {
			const cacheKey = getCacheKey(cloudUrl, helpCenterAri);
			const cached = requestCountCache.get(cacheKey);

			getRequestCount(cloudUrl, helpCenterAri)
				.then((data) => {
					// Always refresh both the request and approvals counts from the
					// latest API response. The Approvals tab is rendered only when
					// `approvals > 0`, so updating to a fresh value of 0 will
					// correctly remove the tab when there is nothing to show.
					const next: RequestCountData = {
						approvals: data.userRequestsCounts.approvalsCount,
						pendingApprovals: data.userRequestsCounts.pendingApprovalsCount,
						request:
							data.userRequestsCounts.allOpenRequestsCount +
							data.userRequestsCounts.pendingApprovalsCount,
					};

					requestCountCache.set(cacheKey, next);
					setRequestCount(next);
				})
				.catch(() => {
					if (cached) {
						// Keep showing the cached values on refresh failure.
						return;
					}
					// fallback count to zero if request count API fails on first load
					const fallback: RequestCountData = {
						approvals: 0,
						pendingApprovals: 0,
						request: 0,
					};
					requestCountCache.set(cacheKey, fallback);
					setRequestCount(fallback);
				});
		} else {
			getRequestCount(cloudUrl, helpCenterAri)
				.then((data) => {
					setRequestCount({
						approvals: data.userRequestsCounts.approvalsCount,
						pendingApprovals: data.userRequestsCounts.pendingApprovalsCount,
						request:
							data.userRequestsCounts.allOpenRequestsCount +
							data.userRequestsCounts.pendingApprovalsCount,
					});
				})
				.catch(() => {
					// fallback count to zero if request count API fails
					setRequestCount({
						approvals: 0,
						pendingApprovals: 0,
						request: 0,
					});
				});
		}
	}, [cloudUrl, helpCenterAri]);

	return (
		<Suspense fallback={<UFOLoadHold name="nav-links-loader" />}>
			<NavLinkItems
				textColor={textColor}
				backgroundColor={backgroundColor}
				requestCount={requestCount}
			/>
		</Suspense>
	);
};
